import { PolicyDefinition } from "./policyTypes";

export const polGov: PolicyDefinition[] = [
  {
    id: "gov-audits",
    code: "GOV-001",
    title: "Audits, Quality Monitoring and Action Plans",
    category: "governance",
    cqcFocus: "Well led",
    sop: false,
    version: "3.0",
    reviewMonths: 12,
    summary: "A planned programme of audits across care, environment and governance, with every finding owned, actioned and tracked to closure.",
    keyPoints: [
      "An audit calendar is published each January.",
      "Every audit has a named owner and a due date.",
      "Findings feed action plans that are reviewed until closed.",
      "Trends are analysed and reported quarterly.",
    ],
    relatedRegulations: [
      "Regulation 17 Good Governance",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Publish the audit calendar in January covering all key areas.",
        "2. Complete audits to schedule and keep the evidence.",
        "3. Score each audit and record findings the same day.",
        "4. Open action plan items with a named owner and due date.",
        "5. Review actions at the monthly meeting until closed.",
        "6. Report quality trends to the provider every quarter.",
        "7. Keep audit records available for inspection.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Managers own their audit areas.",
        "Registered Manager reports trends to the provider.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Audit completion rate tracked monthly.",
        "Action closure rate tracked monthly.",
      ]},
    ],
  },
  {
    id: "gov-incidents",
    code: "GOV-002",
    title: "Incident Reporting, Investigation and Learning",
    category: "governance",
    cqcFocus: "Well led",
    sop: true,
    version: "4.0",
    reviewMonths: 12,
    summary: "All incidents and near misses are reported, graded, investigated where needed and learned from, with CQC notified within timescales.",
    keyPoints: [
      "Every incident and near miss is reported by the end of shift.",
      "Severity is graded on the day.",
      "Serious incidents get a root cause review within 10 working days.",
      "Learning is shared with the whole team.",
    ],
    relatedRegulations: [
      "Regulation 18 Notification of Incidents",
      "Regulation 17 Good Governance",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Record the incident or near miss by the end of shift.",
        "2. Grade severity on the day it is reported.",
        "3. Inform the manager; inform family and GP where relevant.",
        "4. Run a root cause review for serious incidents within 10 working days.",
        "5. Complete CQC notifications within the required timescale.",
        "6. Share learning at the next team meeting.",
        "7. Review incident trends monthly and act on clusters.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "All staff report; no blame culture for near misses.",
        "Registered Manager owns notifications and reviews.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Incident rate and severity trended monthly.",
        "Notification compliance audited quarterly.",
      ]},
    ],
  },
  {
    id: "gov-recruitment",
    code: "GOV-003",
    title: "Safer Recruitment and Vetting",
    category: "governance",
    cqcFocus: "Well led",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Only suitable, vetted and properly inducted people work with residents, with every check completed and evidenced before lone working starts.",
    keyPoints: [
      "Enhanced DBS is obtained before the first working day.",
      "Two references are verified and gaps in history explored.",
      "Right to work and identity are checked and copied.",
      "Induction is completed before lone working.",
    ],
    relatedRegulations: [
      "Regulation 19 Fit and Proper Persons Employed",
      "Safeguarding Vulnerable Groups Act 2006",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Advertise every role with a safeguarding statement.",
        "2. Interview with values based questions and a care scenario.",
        "3. Make a conditional offer pending checks.",
        "4. Complete enhanced DBS, two verified references, right to work and identity checks.",
        "5. Explore any gaps in employment history before start.",
        "6. Complete induction covering all mandatory topics before lone working.",
        "7. Review probation and enrol the DBS update service.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Recruiting managers complete and file every check.",
        "Registered Manager audits recruitment files twice a year.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Recruitment file audit twice a year.",
        "Compliance dashboard reviewed monthly.",
      ]},
    ],
  },
  {
    id: "gov-training",
    code: "GOV-004",
    title: "Supervision, Training and Development",
    category: "governance",
    cqcFocus: "Well led",
    sop: false,
    version: "3.0",
    reviewMonths: 12,
    summary: "Ensures every member of staff is supervised regularly, trained to the mandatory standard and developed with a personal plan.",
    keyPoints: [
      "Supervision happens at least every eight weeks and is recorded.",
      "The training matrix is reviewed monthly and chased.",
      "New staff have a named buddy for the first twelve weeks.",
      "Every staff member has an annual appraisal and development plan.",
    ],
    relatedRegulations: [
      "Regulation 18? No - Regulation 19 Fit and Proper Persons",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Hold supervision at least every eight weeks and record it.",
        "2. Complete mandatory training at induction and refresh per the matrix.",
        "3. Review the training matrix monthly and chase gaps.",
        "4. Hold an annual appraisal with a personal development plan.",
        "5. Give new staff a named buddy for the first twelve weeks.",
        "6. Support staff to gain the Care Certificate and further qualifications.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Managers hold supervisions for their teams.",
        "Registered Manager reviews the matrix monthly.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Training compliance percentage reported monthly.",
        "Supervision completion rate reported monthly.",
      ]},
    ],
  },
  {
    id: "gov-cqc",
    code: "GOV-005",
    title: "CQC Notifications and Registration Matters",
    category: "governance",
    cqcFocus: "Well led",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Statutory notifications are submitted accurately and within timescales, and all registration matters are kept current.",
    keyPoints: [
      "The list of notifiable events is known to all managers.",
      "Notifications go in within timescales with accurate facts.",
      "A notification register records outcomes and learning.",
      "Changes to registration details are notified promptly.",
    ],
    relatedRegulations: [
      "Regulation 18 Notification of Incidents",
      "CQC Registration Regulations 2009",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Keep the list of notifiable events visible to managers.",
        "2. Submit notifications within timescales using accurate facts.",
        "3. Record every notification and its outcome on the register.",
        "4. Notify changes to registered details without delay.",
        "5. Review notifications quarterly for shared learning.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Registered Manager owns all notifications.",
        "Deputy maintains the register.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Notification register reviewed quarterly.",
        "Timescale compliance audited twice a year.",
      ]},
    ],
  },
  {
    id: "gov-staffing",
    code: "GOV-006",
    title: "Safe Staffing and Skill Mix",
    category: "governance",
    cqcFocus: "Well led",
    sop: false,
    version: "2.0",
    reviewMonths: 12,
    summary: "Staffing levels and skill mix are set by resident dependency, reviewed daily and escalated through a contingency plan when short.",
    keyPoints: [
      "The establishment is set using a dependency and acuity tool.",
      "Actual versus planned staffing is reviewed every day.",
      "Shortfalls follow the contingency plan and are recorded.",
      "Agency staff are checked and inducted before starting.",
    ],
    relatedRegulations: [
      "Regulation 19? No - Regulation 16? Use Regulation 17 Good Governance",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Set the staffing establishment using the dependency tool.",
        "2. Review actual versus planned staffing daily.",
        "3. Escalate shortfalls through the contingency plan: bank, agency, manager cover.",
        "4. Check agency staff against the approved list and give a site induction.",
        "5. Record the reason for every shortfall and the action taken.",
        "6. Review staffing hours against incidents quarterly.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Manager on shift owns daily escalation.",
        "Registered Manager reports safe staffing to the provider quarterly.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Agency spend and shortfall trend reviewed monthly.",
        "Safe staffing report to the provider quarterly.",
      ]},
    ],
  },
];